import type { DataTableColumn } from './types';

type Row = Record<string, any>;
type Column = DataTableColumn<Row>;

export interface ExportTable {
  headers: string[];
  rows: unknown[][];
}

const stripHtml = (value: string): string => value.replace(/<[^>]*>/g, '').trim();

/**
 * Flattens the visible columns (master + merged sub-columns) and turns the
 * filtered rows into plain header/cell arrays shared by every export format
 * (CSV, XLSX). A cell hidden by the column's per-row `visible()` is exported
 * empty; currency cells become numbers; strings lose any HTML markup a
 * cellRenderer may have produced; everything else is passed through as-is.
 */
export const prepareExportRows = (columns: Column[], data: Row[]): ExportTable => {
  const flatColumns = columns
    .flatMap((col: Column) => (col.mergedColumns?.length ? [col, ...col.mergedColumns] : [col]))
    .filter((col: Column) => col.data);

  const headers = flatColumns.map((col: Column) => col.title ?? String(col.data));

  const rows = data.map((row: Row) =>
    flatColumns.map((col: Column) => {
      if (col.visible && !col.visible(row)) {
        return '';
      }
      const value = row[col.data as string];
      if (col.renderType === 'currency') {
        return parseFloat(value) || 0;
      }
      if (typeof value === 'string') {
        return stripHtml(value);
      }
      return value;
    })
  );

  return { headers, rows };
};

/** `<prefix>-<yyyy-mm-dd>.<ext>` — the export filename convention. */
export const exportFilename = (prefix: string | undefined, ext: string): string =>
  `${prefix || 'export'}-${new Date().toISOString().split('T')[0]}.${ext}`;
